import { DateTime } from 'luxon';
import { reportTrigger } from '../../../../../particles/api/extension';
import type { ExtensionReportItem } from '../../../../../particles/api/extension';
import { routes } from '../../../../../particles/common';

const CATEGORY_COLORS: Record<string, string> = {
  detection: '#e65100',
  api: '#1565c0',
  selector: '#6a1b9a',
  network: '#c62828',
  auth: '#d32f2f'
};

const TRIGGER_COLORS: Record<string, string> = {
  'Cache clear': '#00897b',
  'Sent by customer': '#3482cf',
  'Error banner': '#757575'
};

const generateRows = (items: ExtensionReportItem[]): TableRow[] =>
  items.map((item) => {
    const byCategory = item.summary?.byCategory || {};
    const categoryChips = Object.entries(byCategory).map(([cat, count]) => ({
      text: `${cat}: ${count}`,
      backgroundColor: CATEGORY_COLORS[cat] || '#757575'
    }));

    const detections = item.recentDetections || [];
    const failedDetections = detections.filter((d) => d.result === 'fail').length;
    const totalDetections = detections.length;

    const topErrorLabels = (item.errors || [])
      .slice(0, 3)
      .map((e) => `${e.label}`)
      .join(', ');

    return {
      id: item.id,
      // A customer-sent report opens in full on the lookup screen.
      ...(item.referenceCode && {
        linkData: {
          to: `${routes.SA_DiagnosticsLookup}?code=${encodeURIComponent(item.referenceCode)}`
        }
      }),
      referenceCode: item.referenceCode || '-',
      trigger: {
        type: 'chip' as const,
        data: [{ text: reportTrigger(item), backgroundColor: TRIGGER_COLORS[reportTrigger(item)] }]
      },
      userName: item.user?.name || '-',
      userEmail: item.user?.email || '-',
      companyName: item.user?.companyName || '-',
      extensionVersion: item.extensionVersion || '-',
      totalErrors: item.totalErrors,
      primaryCategory: item.primaryCategory
        ? {
            type: 'chip' as const,
            data: [
              {
                text: item.primaryCategory,
                backgroundColor: CATEGORY_COLORS[item.primaryCategory] || '#757575'
              }
            ]
          }
        : '-',
      breakdown: categoryChips.length > 0
        ? { type: 'chip' as const, data: categoryChips }
        : '-',
      detections:
        totalDetections > 0
          ? {
              type: 'text' as const,
              data: {
                text: `${failedDetections}/${totalDetections} failed`,
                fontColor: failedDetections > 0 ? '#d32f2f' : '#2e7d32'
              }
            }
          : '-',
      timestamp: item.timestamp
        ? DateTime.fromISO(item.timestamp).toLocaleString(DateTime.DATETIME_MED)
        : '',
      topErrors: topErrorLabels
        ? {
            type: 'text' as const,
            data: {
              text:
                topErrorLabels.length > 80
                  ? topErrorLabels.substring(0, 80) + '...'
                  : topErrorLabels,
              fontColor: '#d32f2f'
            }
          }
        : '-'
    };
  });

export default generateRows;
